import React, { useState } from "react";
import DashboardShell from "@/components/DashboardShell";
import { useProject } from "@/context/ProjectContext";
import api from "@/lib/api";
import { Copy, Check, Download } from "lucide-react";

export default function LlmsTxt() {
  const { current, projects, selectProject } = useProject();
  const [siteName, setSiteName] = useState(current?.name || "");
  const [siteUrl, setSiteUrl] = useState(
    current?.domain ? `https://${current.domain}` : ""
  );
  const [description, setDescription] = useState(
    current?.description || "A short summary of what this site offers."
  );
  const [output, setOutput] = useState("");
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    setLoading(true);
    try {
      const r = await api.post("/schema/llms-txt", {
        site_name: siteName,
        site_url: siteUrl,
        description,
        sections: [
          {
            title: "Primary Resources",
            links: [
              { name: "Home", url: siteUrl, description: "Main entry point" },
              { name: "Blog", url: `${siteUrl}/blog`, description: "Articles and tutorials" },
              { name: "Pricing", url: `${siteUrl}/pricing`, description: "Plan comparison" },
            ],
          },
        ],
      });
      setOutput(r.data.text);
    } finally {
      setLoading(false);
    }
  };

  const copy = async () => {
    await navigator.clipboard.writeText(output);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const download = () => {
    const blob = new Blob([output], { type: "text/plain;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "llms.txt";
    a.click();
  };

  return (
    <DashboardShell project={current} projects={projects} onProjectChange={selectProject}>
      <div className="mb-8">
        <div className="label-eyebrow">Technical</div>
        <h1 className="display mt-2 text-4xl font-black tracking-tighter">
          llms.txt Generator
        </h1>
        <p className="mt-2 text-zinc-600 text-sm max-w-xl">
          The 2026 equivalent of robots.txt — a markdown-style file that tells
          AI crawlers how to interpret and summarise your site.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <section className="card-flat p-5 lg:col-span-5 space-y-4" data-testid="llms-inputs">
          <Field label="Site name" value={siteName} onChange={setSiteName} testid="llms-name" />
          <Field label="Site URL" value={siteUrl} onChange={setSiteUrl} testid="llms-url" />
          <div>
            <label className="label-eyebrow">Description</label>
            <textarea
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              data-testid="llms-desc"
              className="mt-1 w-full bg-white border border-zinc-200 rounded-[4px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black/10"
            />
          </div>
          <button
            onClick={generate}
            disabled={loading || !siteName.trim() || !siteUrl.trim()}
            data-testid="llms-generate"
            className="inline-flex items-center gap-1.5 bg-black text-white text-sm px-4 py-2 rounded-[4px] hover:bg-zinc-800 transition-colors disabled:opacity-50"
          >
            {loading ? (
              <>
                <span className="w-3 h-3 border border-white/40 border-t-white rounded-full spin" />
                Generating…
              </>
            ) : (
              "Generate llms.txt"
            )}
          </button>
        </section>

        <section className="card-flat p-5 lg:col-span-7" data-testid="llms-output">
          <div className="flex items-center justify-between">
            <div className="label-eyebrow">/llms.txt</div>
            {output && (
              <div className="flex items-center gap-2">
                <button
                  onClick={copy}
                  data-testid="llms-copy"
                  className="inline-flex items-center gap-1 text-xs border border-zinc-900 px-2.5 py-1 rounded-[4px] hover:bg-zinc-100"
                >
                  {copied ? <Check size={12} /> : <Copy size={12} />}{" "}
                  {copied ? "Copied" : "Copy"}
                </button>
                <button
                  onClick={download}
                  data-testid="llms-download"
                  className="inline-flex items-center gap-1 text-xs border border-zinc-900 px-2.5 py-1 rounded-[4px] hover:bg-zinc-100"
                >
                  <Download size={12} /> Download
                </button>
              </div>
            )}
          </div>
          <pre className="code-block mt-3 text-[11px] whitespace-pre-wrap break-words min-h-[320px]">
            {output || "# Fill in the fields on the left, then click Generate."}
          </pre>
        </section>
      </div>
    </DashboardShell>
  );
}

function Field({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="label-eyebrow">{label}</label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="mt-1 w-full bg-white border border-zinc-200 rounded-[4px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black/10"
      />
    </div>
  );
}
